<template>
  <div
    class="prevent-select"
    @contextmenu.stop.prevent="contextMenuClick"
    @click.ctrl.prevent="toggleItemSelection(false)"
    @click.shift.prevent="toggleItemSelection(true)"

    :draggable="isDraggableItem(item, asSearchResult)"
    @dragstart="onDragStart($event, item)"
    @dragend="onDragEnd($event, item)"
  >
    <div class="d-flex justify-center" style="width: 200px; height: 112px;" v-if="showPreviews">
      <div v-if="isArchived || isDeleted">
        <PodThumbnail :item="item" />
      </div>
      <router-link
        v-else
        :to="{ path: `/pod/${item.id}` }"
        class="flex-grow-1"
        :aria-label="isArchived || isDeleted ? undefined : item.name"
        :tabindex="-1"
      >
        <PodThumbnail :item="item" />
      </router-link>
    </div>

    <!-- Show a block with a file icon if showPreviews is false -->
    <div v-else class="d-flex justify-center align-center" style="width: 200px; height: 112px; background-color: rgba(0, 0, 0, 0.05);">
      <v-icon size="48">mdi-file-outline</v-icon>
    </div>

    <div class="d-flex justify-space-between" style="margin-top: -112px; max-height: 28px; min-height: 28px;">
      <div class="grid-item-select-wrapper">
        <PodItemSelect
          v-if="!asSearchResult"
          :pod="item"
          :isDeleted="isDeleted"
          @toggleItemSelection="toggleItemSelection"
          class=""
          style="width: 28px; height: 28px; margin-top: -4px; margin-bottom: -24px;"
        />
      </div>

      <div v-if="!asSearchResult">
        <v-chip v-if="isArchived" size="small">
          <v-icon icon="mdi-package-variant-closed-check"></v-icon>
        </v-chip>
        <v-chip size="x-small" v-else-if="getFileType">{{ getFileType }}</v-chip>
        <br/>
        <v-chip size="x-small" v-if="getPodVersions.length > 1">
          {{ $t('routes.node.versionShort') }} {{ getPodVersions.length }}
        </v-chip>
      </div>
    </div>

    <div class="d-flex justify-space-between" style="margin-top: 65px;height: 24px;">
      <div>
        <v-chip size="x-small" v-if="getDuration">
          {{ videoDuration(getDuration) }}
        </v-chip>
      </div>
      <div>
        <v-chip v-if="item.teamonly === accessType.TEAM_ONLY"
                :title="$t('routes.node.accessTypeTeamOnly')" size="x-small">
          <v-icon size="x-small" icon="mdi-account-group-outline"></v-icon>
        </v-chip>
        <v-chip size="x-small" v-if="commentsCount > 0" class="ml-1">
          <v-icon size="x-small" icon="mdi-comment-outline" class="pr-1"></v-icon>
          {{ commentsCount }}
        </v-chip>
        <v-chip
          v-if="item.deletedat !== null && item?.deletedby?.name"
          v-bind:title="$t('common.deletedBy') + ' ' + item?.deletedby?.name"
          label size="x-small"
        >
          <v-icon size="x-small">mdi-account-alert</v-icon>
        </v-chip>
        <RecycleBinGridViewRetentionPeriod v-if="isDeleted" v-bind:pod="item"/>
      </div>
    </div>

    <div class="grid-pod-name text-truncate mt-1">
      <v-tooltip location="bottom" :open-delay="200" :aria-label="item.name">
        <template v-slot:activator="{ props }">
          <div
            v-bind="props"
            class="text-truncate"
            data-cy="list-podItem-name"
          >
            <router-link
              v-if="!isDeleted && !(isArchived && $canControlNodeAndPod())"
              v-bind:is="isArchived ? 'span' : 'router-link'"
              v-bind:to="{ path: `/pod/${item.id}` }"
              class="text-truncate"
              :aria-label="$t('routes.node.a11y.openFile', { name: item.name })"
            >
              {{ item.name }}
            </router-link>
            <span v-else class="text-truncate">
              <b>{{ item.name }}</b>
            </span>
          </div>
        </template>
        <span>{{ item.name }}</span>
      </v-tooltip>
    </div>

    <div class="d-flex mt-1">
      <div class="flex-grow-1 d-flex align-center">
        <div v-if="!item.deletedat" class="d-flex flex-column grid-pod-attributes" style="width: 164px;">
          <div>
            {{ isoToDatetime(item.createdat) }}
          </div>
          <div class="text-truncate">{{ getCreatedBy }}</div>
        </div>

        <div v-else class="d-flex flex-column grid-pod-attributes" style="width: 164px">
          <div>{{ isoToDatetime(item.deletedat) }}</div>
        </div>
      </div>
      <div class="flex-grow-0">
        <v-btn
          icon
          variant="flat"
          rounded="circle"
          density="comfortable"
          color="rgba(0, 0, 0, 0)"
          width="36"
          height="36"
          @click="contextMenuBtnClick"
          :aria-label="$t('routes.node.a11y.openActionsMenu', { name: item.name })"
        >
          <v-icon icon="mdi-dots-vertical"/>
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import { usePodStore } from '@/stores/PodStore.js';
import { useCommonStore } from '@/stores/CommonStore.js';
import { usePodCommentStore } from '@/stores/PodCommentStore.js';
import { useRecycleBinStore } from '@/stores/RecycleBinStore.js';
import PodItemSelect from "../nodeListView/PodItemSelect.vue";
import PodThumbnail from '@/layouts2/components/PodThumbnail.vue';
import { videoDuration } from '@/components/formatters.js';
import { isoToDatetime } from '@/components/formatters.js';
import RecycleBinGridViewRetentionPeriod from "../../../recycle-bin/components/RecycleBinGridViewRetentionPeriod.vue";
import RecycleBinListViewRetentionPeriod
  from '@/routes/recycle-bin/components/RecycleBinListViewRetentionPeriod.vue';

import MovableNodePod from '@/components/mixin/MovableItem';

export default {
  name: "GridPodItem",
  mixins: [MovableNodePod],
  emits: ['toggleItemSelection', 'showContextMenu'],
  components: {
    RecycleBinListViewRetentionPeriod,
    PodThumbnail,
    PodItemSelect,
    RecycleBinGridViewRetentionPeriod,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    asSearchResult: {
      type: Boolean,
      default: false,
    },
    isDeleted: {
      type: Boolean,
      default: false,
    },
    showPreviews: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      adobeMessage: "",
      commentsCount: 0,
    };
  },
  mounted() {
    this.updateCommentsCount();
  },
  methods: {
    isoToDatetime,
    videoDuration,
    async updateCommentsCount() {
      if (!this.$canListPodService()) {
        return;
      }

      if (this.item.archived || this.isDeleted || this.asSearchResult) {
        this.commentsCount = 0;
        return;
      }

      if (this.item.commentsCount !== undefined) {
        this.commentsCount = this.item.commentsCount;
        return;
      }

      try {
        const podCommentStore = usePodCommentStore();
        const comments = await podCommentStore.getCommentsStateless(this.item.id);
        this.commentsCount = Array.from(comments).length;
      } catch (error) {
        console.error('Error fetching comments:', error);
        this.commentsCount = 0;
      }
    },
    toggleItemSelection(holdingShift) {
      const payload = {
        holdingShift: holdingShift,
        item: this.item
      };

      this.$emit("toggleItemSelection", payload);
    },
    contextMenuClick(event) {
      const eventData = {
        contextMenuPod: this.item,
        contextMenuX: event.clientX,
        contextMenuY: event.clientY,
      };

      this.$emit('showContextMenu', eventData);
    },
    contextMenuBtnClick(event) {
      const eventData = {
        contextMenuPod: this.item,
        contextMenuX: event.clientX - event.offsetX + event.target.offsetWidth,
        contextMenuY: event.clientY - event.offsetY + event.target.offsetHeight,
      };

      this.$emit('showContextMenu', eventData);
    },
  },
  computed: {
    isArchived() {
      return this.item.archived !== 0;
    },
    getPodVersions() {
      if (this.isDeleted) {
        const recycleBinStore = useRecycleBinStore();
        return recycleBinStore.getDeletedPodVersions(this.item.id);
      }

      const podStore = usePodStore();
      return podStore.getPodVersions(this.item.id);
    },
    getCreatedBy() {
      if (this.item.jsondata?.uploaderName) {
        return `${this.$t("routes.node.externalUploader")}: ${this.item.jsondata.uploaderName}`;
      }

      if (this.item) {
        if (this.item.createdby) {
          return `${this.item.createdby.firstname} ${this.item.createdby.lastname}`;
        }

        return this.$t('common.deletedUser');
      }

      return this.$t("routes.node.unknownUploader");
    },
    getDuration() {
      if (
        this.item &&
        this.item.asset &&
        this.item.asset.web &&
        this.item.asset.web[0] &&
        this.item.asset.web[0].duration
      ) {
        return this.item.asset.web[0].duration;
      }

      return false;
    },
    getFileType() {
      if (
        this.item &&
        this.item.asset &&
        this.item.asset.raw &&
        this.item.asset.raw[0] &&
        this.item.asset.raw[0].filetype
      ) {
        return this.item.asset.raw[0].filetype;
      }

      return false;
    },
    accessType() {
      const commonStore = useCommonStore();
      return commonStore.accessType;
    },
  },
};
</script>

<style lang="scss" scoped>
.grid-pod-name {
  width: 100%;
  font-size: 12px;
  line-height: 1.2;

  a {
    max-width: 100%;
    display: inline-block;
  }
}

.grid-pod-attributes {
  font-size: 12px;
  line-height: 1.2;
}

.grid-item-select-wrapper {
  border-radius: 4px;
}

.v-theme--dark .grid-item-select-wrapper {
  background-color: rgba(85, 85, 85, 0.7) !important;
}
.v-theme--light .grid-item-select-wrapper {
  background-color: rgba(224, 224, 224, 0.7) !important;
}
</style>
